
/* ============================== src/components/Modals/YearCalendarModal.jsx ============================== */
// src/components/Modals/YearCalendarModal.jsx
import { useState, useMemo } from "react";
import { ModalShell } from "../ModalShell";
import { Ic } from "../Icons";
import { ICONS } from "../iconsData";
import { iso, fmtDMY, parseISO, pad2 } from "../../utils/date";
import { useProductionCalendarQuery } from "../../api/queries/production-calendar";

const MONTH_NAMES = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

const WEEKDAY_LABELS = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

const mondayIndex = (jsDay) => (jsDay + 6) % 7;

export default function YearCalendarModal({ onClose }) {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const { data: calendarData = [] } = useProductionCalendarQuery();

  const todayIso = iso(new Date());

  const months = useMemo(() => {
    const yearData = calendarData.find((d) => d.year === year) || {
      holidays: [],
      shortDays: [],
    };
    const holidays = new Set(yearData.holidays);
    const shortDays = new Set(yearData.shortDays);
    const defaultWeekends = new Set([0, 6]); // сб, вс

    const result = [];
    for (let m = 0; m < 12; m++) {
      const daysInMonth = new Date(year, m + 1, 0).getDate();
      const days = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const dayIso = `${year}-${pad2(m + 1)}-${pad2(d)}`;
        const jsDay = parseISO(dayIso).getDay();

        let status = "workday";
        if (holidays.has(dayIso)) status = "holiday";
        else if (defaultWeekends.has(jsDay)) status = "weekend";
        else if (shortDays.has(dayIso)) status = "shortday";

        days.push({ iso: dayIso, day: d, status });
      }
      result.push({ month: m, days });
    }
    return result;
  }, [calendarData, year]);

  const STATUS_CLASS = {
    workday: "yc-day--workday",
    shortday: "yc-day--shortday",
    weekend: "yc-day--weekend",
    holiday: "yc-day--holiday",
  };

  return (
    <ModalShell
      title="Производственный календарь"
      onClose={onClose}
      width={1400}
      className="modal-year-calendar"
      showSave={false}
    >
      <div className="yc-nav">
        <button
          type="button"
          className="icon-btn"
          onClick={() => setYear((y) => y - 1)}
          title="Предыдущий год"
        >
          <Ic d={ICONS.left} size={16} />
        </button>
        <div className="yc-year">{year}</div>
        <button
          type="button"
          className="icon-btn"
          onClick={() => setYear((y) => y + 1)}
          title="Следующий год"
        >
          <Ic d={ICONS.right} size={16} />
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => setYear(new Date().getFullYear())}
        >
          Сегодня
        </button>
        <span className="yc-today-hint">Сегодня: {fmtDMY(todayIso)}</span>
      </div>

      <div className="yc-grid">
        {months.map((m, idx) => (
          <Month
            key={m.month}
            month={m}
            name={MONTH_NAMES[idx]}
            year={year}
            todayIso={todayIso}
            statusClass={STATUS_CLASS}
          />
        ))}
      </div>

      <div className="yc-legend">
        <span className="yc-legend-item">
          <span className="yc-legend-dot yc-legend-dot--workday" /> Рабочий
        </span>
        <span className="yc-legend-item">
          <span className="yc-legend-dot yc-legend-dot--shortday" /> Сокращённый
        </span>
        <span className="yc-legend-item">
          <span className="yc-legend-dot yc-legend-dot--weekend" />{" "}
          Выходной/Праздничный
        </span>
        <span className="yc-legend-item">
          <span className="yc-legend-dot yc-legend-dot--today" /> Сегодня
        </span>
      </div>
    </ModalShell>
  );
}

function Month({ month, name, year, todayIso, statusClass }) {
  const firstWeekday = mondayIndex(new Date(year, month.month, 1).getDay());
  const blanks = Array.from({ length: firstWeekday }, (_, i) => i);

  return (
    <div className="yc-month">
      <div className="yc-month-title">{name}</div>
      <div className="yc-weekdays">
        {WEEKDAY_LABELS.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      <div className="yc-days">
        {blanks.map((i) => (
          <span key={`b${i}`} className="yc-day yc-day--empty" />
        ))}
        {month.days.map((d) => {
          const classes = ["yc-day", statusClass[d.status]];
          if (d.iso === todayIso) classes.push("yc-day--today");
          return (
            <span key={d.iso} className={classes.join(" ")}>
              {d.day}
            </span>
          );
        })}
      </div>
    </div>
  );
}
